/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createRef, useEffect, useState } from 'react';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  associatedPlacementReducer,
  associatedPlacementSliceKey,
} from 'app/components/Placement/AssociatedPlacements/store/associatedPlacements.redux';
import { associatedPlacementsSaga } from 'app/components/Placement/AssociatedPlacements/store/associatedPlacements.saga';
import { PlacementSearchFilterName, placementViewInfo } from 'app/components/GlobalSearch/Placement/SearchUtils';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notOrderColumns } from 'app/components/Placement/AssociatedPlacements/store/utils';
import { FormProvider, useForm } from 'react-hook-form';
import SearchGridWrapper from 'app/layout/pages/SearchGridWrapper';
import { OrderPlacementSearchFilter } from './OrderPlacementsFilters';
import { OrderPlacementChips } from './OrderPlacementsChips';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { useDispatch, useSelector } from 'react-redux';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { revalidateChipSelection } from '@AMIEWEB/GlobalSearch/helper';
import { selectFilters, selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { gridSelectionActions } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.redux';
import { defaultFilterValues } from './filterUtils';
import { OrderPlacementGrid } from './OrderPlacementsGrid';
import {
  userDevicePreferenceActions,
  userDevicePreferenceKey,
  userDevicePreferenceReducer,
} from 'oidc/UserDevicePreference/userPreference.redux';
import { RootState } from 'types';
import { userDevicePreferenceSaga } from 'oidc/UserDevicePreference/userPreference.saga';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { useDecision } from '@optimizely/react-sdk';
import { ff_candidate_communication_grid } from 'app/constants/FeatureFlags/Tasks/Notifications/Keys';
import { ff_placement_region_fields } from 'app/constants/FeatureFlags/Placement/Keys';

export const OrderPlacementsWrapper = () => {
  useInjectReducer({ key: associatedPlacementSliceKey, reducer: associatedPlacementReducer });
  useInjectSaga({ key: associatedPlacementSliceKey, saga: associatedPlacementsSaga });
  useInjectReducer({ key: userDevicePreferenceKey, reducer: userDevicePreferenceReducer });
  useInjectSaga({ key: userDevicePreferenceKey, saga: userDevicePreferenceSaga });
  const { t } = useTranslation();
  const [candidateCommunicationConsentFlag] = useDecision(ff_candidate_communication_grid);
  const filterPreferencesId = 'order-placement-filter';
  const { id: orderId } = useParams<{ id: string }>();
  const [regionFlag] = useDecision(ff_placement_region_fields);

  const [columns, setColumns] = React.useState(
    placementViewInfo(t, [], candidateCommunicationConsentFlag.enabled).filter(
      column =>
        !(regionFlag.enabled ? notOrderColumns : [...notOrderColumns, 'region']).some(
          notValue => notValue === column.field,
        ),
    ),
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [defaultColumns, setDefaultColumns] = React.useState<any>(
    placementViewInfo(t, [], candidateCommunicationConsentFlag.enabled).filter(
      column =>
        !(regionFlag.enabled ? notOrderColumns : [...notOrderColumns, 'region']).some(
          notValue => notValue === column.field,
        ),
    ),
  );
  const formMethods = useForm({ defaultValues: defaultFilterValues });
  const { handleSubmit, reset } = formMethods;
  const dispatch = useDispatch();
  const gridState = useSelector(selectGridState);
  const appliedFilterValues = useSelector(selectFilters);
  const [searchFilter, setSearchFilter] = useState<any>({});
  const filterPreference = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === filterPreferencesId);
  });
  const { loaded: preferencesLoaded } = useSelector(selectUserPreference);
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const onApplyFilter = (filterValues, newSelectedChipFilters?) => {
    dispatch(gridStateActions.setPageNumber(1));
    const selectedChipFilters =
      newSelectedChipFilters ||
      revalidateChipSelection(filterValues, gridState.filterAttributes?.selectedChipFilters || []);
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(filterValues ?? defaultFilterValues),
        selectedChipFilters,
      }),
    );
  };
  const onApplyFilterandSave = (filterValues, newSelectedChipFilters?) => {
    const preferenceData = {
      id: filterPreferencesId,
      value: {
        filterValues: filterValues,
        newSelectedChipFilters: newSelectedChipFilters ?? appliedFilterValues?.selectedChipFilters,
      },
    };
    onApplyFilter(preferenceData?.value?.filterValues, newSelectedChipFilters);
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
  };

  useEffect(() => {
    if (preferencesLoaded) {
      const payload = filterPreference?.value ?? { filterValues: defaultFilterValues };
      reset(payload?.filterValues);
      onApplyFilter(payload?.filterValues, payload?.newSelectedChipFilters);
    }

    return () => {
      dispatch(gridStateActions.reset());
      dispatch(globalSearchActions.reset());
      dispatch(globalActions.closeBanner());
      dispatch(
        gridSelectionActions.setGridSelections({
          selectedData: [],
        }),
      );
    };
  }, [filterPreference]);
  useEffect(() => {
    if (appliedFilterValues) {
      const selectedFilterValues = {
        filter: {
          [PlacementSearchFilterName.oid]: [{ id: orderId, name: orderId, value: orderId }],
          ...appliedFilterValues?.filters,
        },

        selectedChipFilters: { ...appliedFilterValues?.selectedChipFilters },
      };
      setSearchFilter(selectedFilterValues);
    }
  }, [appliedFilterValues]);
  return (
    <React.Fragment>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(filter => onApplyFilterandSave(filter))}>
          <SearchGridWrapper
            filters={<OrderPlacementSearchFilter onClickHandler={onApplyFilterandSave} />}
            table={
              <OrderPlacementGrid
                defaultColumns={defaultColumns}
                columns={columns}
                setColumns={setColumns}
                rootFilter={searchFilter}
              />
            }
            chips={<OrderPlacementChips onApplyFilter={onApplyFilterandSave} category={SearchType.orderPlacement} />}
            tableMinHeight={430}
            loading={false}
          />
          <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
